/**
 * ExhibitionDetail — tabbed detail view for one exhibition.
 * Tabs: Overview · Booth & Team · Contacts · Goals & Footfall ·
 *       Budget · Leads.
 *
 * Every editable section auto-saves on blur / patch to keep the flow
 * frictionless. Compact, dense layout — expo teams work fast.
 *
 * Feb 2026 · R15.
 */
import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, Save, Plus, Trash2, MapPin, Users, Target, Wallet, UserPlus, Building2, Trophy, Send, ListChecks, TrendingUp } from "lucide-react";

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "booth", label: "Booth & Team" },
  { key: "contacts", label: "Contacts" },
  { key: "goals", label: "Goals & Footfall" },
  { key: "budget", label: "Budget" },
  { key: "playbook", label: "Playbook" },
  { key: "leads", label: "Leads" },
  { key: "roi", label: "ROI" },
];

const STATUSES = ["planned", "confirmed", "onsite", "closed", "cancelled"];

export default function ExhibitionDetail() {
  const { id } = useParams();
  const { activeWorkspaceId: ws } = useAuth();
  const navigate = useNavigate();
  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [captureOpen, setCaptureOpen] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);

  const load = useCallback(async () => {
    if (!ws) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/workspaces/${ws}/exhibitions/${id}`);
      setRow(data);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Load failed"));
      navigate("/app/exhibitions");
    } finally { setLoading(false); }
  }, [ws, id, navigate]);
  useEffect(() => { load(); }, [load]);

  const save = async (patch) => {
    try {
      const { data } = await api.patch(`/workspaces/${ws}/exhibitions/${id}`, patch);
      setRow(data);
      toast.success("Saved");
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this exhibition?")) return;
    try {
      await api.delete(`/workspaces/${ws}/exhibitions/${id}`);
      toast.success("Deleted");
      navigate("/app/exhibitions");
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  if (loading || !row) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;

  const totalBudget = (row.budget_items || []).reduce((s, b) => s + (b.planned_amount || 0), 0);
  const actualBudget = (row.budget_items || []).reduce((s, b) => s + (b.actual_amount || 0), 0);

  return (
    <div className="p-6 space-y-4" data-testid="exhibition-detail-page">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" className="rounded-none" onClick={() => navigate("/app/exhibitions")}>
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
      </div>
      <PageHeader
        title={row.name}
        description={`${row.location_type} · ${row.city || "—"}${row.country_code ? ` · ${row.country_code}` : ""} · ${row.start_date} → ${row.end_date}`}
        actions={
          <div className="flex items-center gap-2">
            <Select value={row.status} onValueChange={(v) => save({ status: v })}>
              <SelectTrigger className="h-9 w-36 rounded-none" data-testid="exhibition-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <Button variant="outline" size="sm" className="rounded-none" onClick={() => setCaptureOpen(true)} data-testid="exhibition-capture-btn">
              <UserPlus className="w-3.5 h-3.5 mr-1" /> Capture lead
            </Button>
            <Button variant="outline" size="sm" className="rounded-none border-emerald-500 text-emerald-700 hover:bg-emerald-50" onClick={() => setConvertOpen(true)} data-testid="exhibition-convert-btn">
              <Send className="w-3.5 h-3.5 mr-1" /> Convert to campaign
            </Button>
            <Button variant="outline" size="sm" className="rounded-none text-rose-600 border-rose-300" onClick={remove} data-testid="exhibition-delete-btn">
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <Kpi icon={<Building2 className="w-4 h-4 text-neutral-600" />} label="Booth" value={row.booth?.number || "—"} sub={row.booth?.size_sqm ? `${row.booth.size_sqm} sqm` : null} />
        <Kpi icon={<Users className="w-4 h-4 text-blue-500" />} label="Team" value={(row.team || []).length} />
        <Kpi icon={<Trophy className="w-4 h-4 text-amber-500" />} label="Leads captured" value={row.captured_leads_count || 0} sub={row.goals?.target_leads ? `target ${row.goals.target_leads}` : null} />
        <Kpi icon={<Wallet className="w-4 h-4 text-emerald-500" />} label="Budget planned" value={totalBudget.toLocaleString()} sub={`spent ${actualBudget.toLocaleString()}`} />
        <Kpi icon={<Target className="w-4 h-4 text-violet-500" />} label="Footfall exp." value={row.footfall?.expected_total || "—"} sub={row.footfall?.actual_total ? `actual ${row.footfall.actual_total}` : null} />
      </div>

      <div className="flex items-center gap-1 border-b">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} className={`px-3 py-2 text-sm border-b-2 ${tab === t.key ? "border-primary text-primary" : "border-transparent text-neutral-600 hover:text-neutral-900"}`} data-testid={`exhibition-tab-${t.key}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && <OverviewTab row={row} save={save} ws={ws} />}
      {tab === "booth" && <BoothTeamTab row={row} save={save} />}
      {tab === "contacts" && <ContactsTab row={row} save={save} />}
      {tab === "goals" && <GoalsFootfallTab row={row} save={save} />}
      {tab === "budget" && <BudgetTab row={row} save={save} />}
      {tab === "playbook" && <PlaybookTab row={row} ws={ws} onReload={load} />}
      {tab === "leads" && <LeadsTab row={row} ws={ws} onOpenCapture={() => setCaptureOpen(true)} />}
      {tab === "roi" && <RoiTab row={row} ws={ws} />}

      {captureOpen && <CaptureLeadDialog ws={ws} eid={id} onClose={() => setCaptureOpen(false)} onDone={() => { setCaptureOpen(false); load(); }} />}
      {convertOpen && <ConvertToCampaignDialog ws={ws} eid={id} exhibitionName={row.name} onClose={() => setConvertOpen(false)} onDone={() => { setConvertOpen(false); load(); }} />}
    </div>
  );
}

function Kpi({ icon, label, value, sub }) {
  return (
    <div className="border p-3">
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-muted-foreground">{icon}{label}</div>
      <div className="text-xl font-mono mt-1">{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground">{sub}</div>}
    </div>
  );
}

// ─────────────── Tab: Overview ───────────────

function OverviewTab({ row, save, ws }) {
  const [form, setForm] = useState(row);
  useEffect(() => { setForm(row); }, [row]);
  const patch = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const commit = (k) => save({ [k]: form[k] });
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="border p-4 space-y-3">
        <div className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">Basic</div>
        <div>
          <Label className="text-xs">Name</Label>
          <Input value={form.name || ""} onChange={e => patch("name", e.target.value)} onBlur={() => commit("name")} className="h-9 rounded-none" />
        </div>
        <div>
          <Label className="text-xs">Description</Label>
          <Textarea rows={3} value={form.description || ""} onChange={e => patch("description", e.target.value)} onBlur={() => commit("description")} className="rounded-none" />
        </div>
        <div>
          <Label className="text-xs">Event website</Label>
          <Input value={form.event_website || ""} onChange={e => patch("event_website", e.target.value)} onBlur={() => commit("event_website")} className="h-9 rounded-none" placeholder="https://" />
        </div>
        <div>
          <Label className="text-xs">Tags (comma-separated)</Label>
          <Input value={(form.tags || []).join(", ")} onChange={e => patch("tags", e.target.value.split(",").map(s => s.trim()).filter(Boolean))} onBlur={() => commit("tags")} className="h-9 rounded-none" />
        </div>
      </div>

      <div className="border p-4 space-y-3">
        <div className="text-xs font-semibold text-muted-foreground uppercase tracking-widest flex items-center gap-1">
          <MapPin className="w-3 h-3" /> Location & Dates
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <Label className="text-xs">Type</Label>
            <Select value={form.location_type} onValueChange={v => { patch("location_type", v); save({ location_type: v }); }}>
              <SelectTrigger className="h-9 rounded-none"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="local">Local</SelectItem>
                <SelectItem value="domestic">Domestic</SelectItem>
                <SelectItem value="international">International</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label className="text-xs">City</Label>
            <Input value={form.city || ""} onChange={e => patch("city", e.target.value)} onBlur={() => commit("city")} className="h-9 rounded-none" />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <Label className="text-xs">Country</Label>
            <Input maxLength={3} value={form.country_code || ""} onChange={e => patch("country_code", e.target.value.toUpperCase())} onBlur={() => commit("country_code")} className="h-9 rounded-none font-mono uppercase" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs">State/Region</Label>
            <Input value={form.state || ""} onChange={e => patch("state", e.target.value)} onBlur={() => commit("state")} className="h-9 rounded-none" />
          </div>
        </div>
        <div>
          <Label className="text-xs">Venue name</Label>
          <Input value={form.venue_name || ""} onChange={e => patch("venue_name", e.target.value)} onBlur={() => commit("venue_name")} className="h-9 rounded-none" />
        </div>
        <div>
          <Label className="text-xs">Venue address</Label>
          <Input value={form.venue_address || ""} onChange={e => patch("venue_address", e.target.value)} onBlur={() => commit("venue_address")} className="h-9 rounded-none" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs">Start date</Label>
            <Input type="date" value={form.start_date || ""} onChange={e => patch("start_date", e.target.value)} onBlur={() => commit("start_date")} className="h-9 rounded-none" />
          </div>
          <div>
            <Label className="text-xs">End date</Label>
            <Input type="date" value={form.end_date || ""} onChange={e => patch("end_date", e.target.value)} onBlur={() => commit("end_date")} className="h-9 rounded-none" />
          </div>
        </div>
      </div>

      <div className="border p-4 space-y-3 md:col-span-2">
        <div className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">Integrations</div>
        <div className="grid grid-cols-2 gap-2">
          <IntegrationLink label="Linked Project" idField="linked_project_id" value={form.linked_project_id} save={save} ws={ws} kind="projects" />
          <IntegrationLink label="Linked Campaign" idField="linked_campaign_id" value={form.linked_campaign_id} save={save} ws={ws} kind="campaigns" />
        </div>
      </div>

      <div className="border p-4 space-y-3">
        <div className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">Sponsorship</div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs">Tier</Label>
            <Select value={form.sponsorship?.tier || ""} onValueChange={v => save({ sponsorship: { ...(form.sponsorship || {}), tier: v } })}>
              <SelectTrigger className="h-9 rounded-none"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                {["platinum", "gold", "silver", "bronze", "exhibitor", "attendee"].map(t => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Package name</Label>
            <Input value={form.sponsorship?.package_name || ""} onChange={e => patch("sponsorship", { ...(form.sponsorship || {}), package_name: e.target.value })} onBlur={() => save({ sponsorship: form.sponsorship })} className="h-9 rounded-none" />
          </div>
        </div>
        <div>
          <Label className="text-xs">Sponsor amount</Label>
          <Input type="number" value={form.sponsorship?.sponsor_amount || ""} onChange={e => patch("sponsorship", { ...(form.sponsorship || {}), sponsor_amount: Number(e.target.value) || 0 })} onBlur={() => save({ sponsorship: form.sponsorship })} className="h-9 rounded-none" />
        </div>
      </div>
    </div>
  );
}

function IntegrationLink({ label, idField, value, save, ws, kind }) {
  const [options, setOptions] = useState([]);
  useEffect(() => {
    if (kind === "projects") {
      api.get(`/workspaces/${ws}/projects`).then(r => setOptions((r.data || []).map(p => ({ id: p.id, name: p.name }))));
    } else if (kind === "campaigns") {
      api.get(`/workspaces/${ws}/marketing/campaigns`).then(r => setOptions((r.data || []).map(c => ({ id: c.id, name: c.name }))));
    }
  }, [ws, kind]);
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Select value={value || ""} onValueChange={v => save({ [idField]: v || null })}>
        <SelectTrigger className="h-9 rounded-none"><SelectValue placeholder="— none —" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__">— none —</SelectItem>
          {options.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

// ─────────────── Tab: Booth & Team ───────────────

function BoothTeamTab({ row, save }) {
  const [booth, setBooth] = useState(row.booth || {});
  const [team, setTeam] = useState(row.team || []);
  useEffect(() => { setBooth(row.booth || {}); setTeam(row.team || []); }, [row]);

  const saveBooth = () => save({ booth });
  const addMember = () => setTeam(t => [...t, { name: "", role: "sales" }]);
  const rmMember = (i) => { const t = team.filter((_, idx) => idx !== i); setTeam(t); save({ team: t }); };
  const updMember = (i, patch) => setTeam(t => t.map((m, idx) => idx === i ? { ...m, ...patch } : m));
  const saveTeam = () => save({ team });

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="border p-4 space-y-2">
        <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Booth</div>
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="text-xs">Number</Label><Input value={booth.number || ""} onChange={e => setBooth({ ...booth, number: e.target.value })} onBlur={saveBooth} className="h-9 rounded-none" /></div>
          <div><Label className="text-xs">Size (sqm)</Label><Input type="number" value={booth.size_sqm || ""} onChange={e => setBooth({ ...booth, size_sqm: Number(e.target.value) || null })} onBlur={saveBooth} className="h-9 rounded-none" /></div>
        </div>
        <div><Label className="text-xs">Location in hall</Label><Input value={booth.location_in_hall || ""} onChange={e => setBooth({ ...booth, location_in_hall: e.target.value })} onBlur={saveBooth} className="h-9 rounded-none" placeholder="Hall A, Row 3" /></div>
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="text-xs">Cost amount</Label><Input type="number" value={booth.cost_amount || ""} onChange={e => setBooth({ ...booth, cost_amount: Number(e.target.value) || null })} onBlur={saveBooth} className="h-9 rounded-none" /></div>
          <div><Label className="text-xs">Currency</Label><Input value={booth.cost_currency || "INR"} onChange={e => setBooth({ ...booth, cost_currency: e.target.value.toUpperCase() })} onBlur={saveBooth} className="h-9 rounded-none font-mono uppercase" /></div>
        </div>
        <div><Label className="text-xs">Floor plan URL</Label><Input value={booth.floor_plan_url || ""} onChange={e => setBooth({ ...booth, floor_plan_url: e.target.value })} onBlur={saveBooth} className="h-9 rounded-none" placeholder="https://" /></div>
        <div><Label className="text-xs">Layout notes</Label><Textarea rows={2} value={booth.layout_notes || ""} onChange={e => setBooth({ ...booth, layout_notes: e.target.value })} onBlur={saveBooth} className="rounded-none" /></div>
      </div>

      <div className="border p-4 space-y-2">
        <div className="flex items-center justify-between">
          <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Team ({team.length})</div>
          <Button size="sm" className="rounded-none h-7" onClick={addMember} data-testid="exhibition-team-add"><Plus className="w-3 h-3 mr-1" /> Add</Button>
        </div>
        {team.length === 0 && <div className="text-xs text-muted-foreground italic">No team members yet.</div>}
        {team.map((m, i) => (
          <div key={i} className="grid grid-cols-[1fr_120px_36px] gap-1 items-end border p-2" data-testid={`exhibition-team-${i}`}>
            <div><Label className="text-[10px]">Name</Label><Input value={m.name} onChange={e => updMember(i, { name: e.target.value })} onBlur={saveTeam} className="h-8 rounded-none text-xs" /></div>
            <div><Label className="text-[10px]">Role</Label>
              <Select value={m.role} onValueChange={v => { updMember(i, { role: v }); setTimeout(saveTeam, 0); }}>
                <SelectTrigger className="h-8 rounded-none text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{["lead", "sales", "demo", "support", "logistics", "designer", "photographer"].map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <button onClick={() => rmMember(i)} className="h-8 border text-rose-600 hover:bg-rose-50" data-testid={`exhibition-team-del-${i}`}><Trash2 className="w-3 h-3 mx-auto" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────── Tab: Contacts ───────────────

function ContactsTab({ row, save }) {
  const [contacts, setContacts] = useState(row.contacts || []);
  useEffect(() => setContacts(row.contacts || []), [row]);
  const add = () => setContacts(c => [...c, { name: "" }]);
  const rm = (i) => { const c = contacts.filter((_, idx) => idx !== i); setContacts(c); save({ contacts: c }); };
  const upd = (i, patch) => setContacts(c => c.map((x, idx) => idx === i ? { ...x, ...patch } : x));
  const commit = () => save({ contacts });
  return (
    <div className="border p-4 space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Key contacts ({contacts.length})</div>
        <Button size="sm" className="rounded-none h-7" onClick={add} data-testid="exhibition-contact-add"><Plus className="w-3 h-3 mr-1" /> Add contact</Button>
      </div>
      {contacts.length === 0 && <div className="text-xs text-muted-foreground italic">No contacts yet. Add event organisers, booth partners, or vendors.</div>}
      {contacts.map((c, i) => (
        <div key={i} className="border p-2 grid grid-cols-[1fr_1fr_1fr_1fr_36px] gap-1 items-end" data-testid={`exhibition-contact-${i}`}>
          <div><Label className="text-[10px]">Name</Label><Input value={c.name} onChange={e => upd(i, { name: e.target.value })} onBlur={commit} className="h-8 rounded-none text-xs" /></div>
          <div><Label className="text-[10px]">Org / Role</Label><Input value={c.org || ""} onChange={e => upd(i, { org: e.target.value })} onBlur={commit} className="h-8 rounded-none text-xs" /></div>
          <div><Label className="text-[10px]">Email</Label><Input type="email" value={c.email || ""} onChange={e => upd(i, { email: e.target.value })} onBlur={commit} className="h-8 rounded-none text-xs" /></div>
          <div><Label className="text-[10px]">Phone</Label><Input value={c.phone || ""} onChange={e => upd(i, { phone: e.target.value })} onBlur={commit} className="h-8 rounded-none text-xs" /></div>
          <button onClick={() => rm(i)} className="h-8 border text-rose-600 hover:bg-rose-50"><Trash2 className="w-3 h-3 mx-auto" /></button>
        </div>
      ))}
    </div>
  );
}

// ─────────────── Tab: Goals & Footfall ───────────────

function GoalsFootfallTab({ row, save }) {
  const [goals, setGoals] = useState(row.goals || {});
  const [ff, setFf] = useState(row.footfall || {});
  useEffect(() => { setGoals(row.goals || {}); setFf(row.footfall || {}); }, [row]);
  const gc = () => save({ goals });
  const fc = () => save({ footfall: ff });
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="border p-4 space-y-2">
        <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Goals</div>
        <div className="grid grid-cols-3 gap-2">
          <div><Label className="text-xs">Target leads</Label><Input type="number" value={goals.target_leads || ""} onChange={e => setGoals({ ...goals, target_leads: Number(e.target.value) || null })} onBlur={gc} className="h-9 rounded-none" data-testid="goals-target-leads" /></div>
          <div><Label className="text-xs">Target meetings</Label><Input type="number" value={goals.target_meetings || ""} onChange={e => setGoals({ ...goals, target_meetings: Number(e.target.value) || null })} onBlur={gc} className="h-9 rounded-none" /></div>
          <div><Label className="text-xs">Target demos</Label><Input type="number" value={goals.target_demos || ""} onChange={e => setGoals({ ...goals, target_demos: Number(e.target.value) || null })} onBlur={gc} className="h-9 rounded-none" /></div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div className="col-span-2"><Label className="text-xs">Revenue target</Label><Input type="number" value={goals.revenue_target_amount || ""} onChange={e => setGoals({ ...goals, revenue_target_amount: Number(e.target.value) || null })} onBlur={gc} className="h-9 rounded-none" /></div>
          <div><Label className="text-xs">Currency</Label><Input value={goals.revenue_target_currency || "INR"} onChange={e => setGoals({ ...goals, revenue_target_currency: e.target.value.toUpperCase() })} onBlur={gc} className="h-9 rounded-none uppercase font-mono" /></div>
        </div>
        <div><Label className="text-xs">Strategic notes</Label><Textarea rows={3} value={goals.strategic_notes || ""} onChange={e => setGoals({ ...goals, strategic_notes: e.target.value })} onBlur={gc} className="rounded-none" placeholder="What does success look like? Which accounts are we targeting?" /></div>
      </div>

      <div className="border p-4 space-y-2">
        <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Footfall</div>
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="text-xs">Expected total</Label><Input type="number" value={ff.expected_total || ""} onChange={e => setFf({ ...ff, expected_total: Number(e.target.value) || null })} onBlur={fc} className="h-9 rounded-none" /></div>
          <div><Label className="text-xs">Expected qualified</Label><Input type="number" value={ff.expected_qualified || ""} onChange={e => setFf({ ...ff, expected_qualified: Number(e.target.value) || null })} onBlur={fc} className="h-9 rounded-none" /></div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="text-xs">Actual total</Label><Input type="number" value={ff.actual_total || ""} onChange={e => setFf({ ...ff, actual_total: Number(e.target.value) || null })} onBlur={fc} className="h-9 rounded-none" /></div>
          <div><Label className="text-xs">Actual qualified</Label><Input type="number" value={ff.actual_qualified || ""} onChange={e => setFf({ ...ff, actual_qualified: Number(e.target.value) || null })} onBlur={fc} className="h-9 rounded-none" /></div>
        </div>
      </div>
    </div>
  );
}

// ─────────────── Tab: Budget ───────────────

function BudgetTab({ row, save }) {
  const [items, setItems] = useState(row.budget_items || []);
  useEffect(() => setItems(row.budget_items || []), [row]);
  const add = () => setItems(x => [...x, { category: "", description: "", planned_amount: 0 }]);
  const upd = (i, patch) => setItems(x => x.map((it, idx) => idx === i ? { ...it, ...patch } : it));
  const rm = (i) => { const x = items.filter((_, idx) => idx !== i); setItems(x); save({ budget_items: x }); };
  const commit = () => save({ budget_items: items });
  const totalPlanned = items.reduce((s, it) => s + (Number(it.planned_amount) || 0), 0);
  const totalActual = items.reduce((s, it) => s + (Number(it.actual_amount) || 0), 0);
  return (
    <div className="border p-4 space-y-2">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Budget line-items ({items.length})</div>
          <div className="text-xs mt-1">Planned <b className="font-mono">{totalPlanned.toLocaleString()}</b> · Actual <b className="font-mono">{totalActual.toLocaleString()}</b> · Delta <b className={`font-mono ${totalActual > totalPlanned ? "text-rose-600" : "text-emerald-600"}`}>{(totalActual - totalPlanned).toLocaleString()}</b></div>
        </div>
        <Button size="sm" className="rounded-none h-7" onClick={add} data-testid="exhibition-budget-add"><Plus className="w-3 h-3 mr-1" /> Add line</Button>
      </div>
      {items.length === 0 && <div className="text-xs text-muted-foreground italic pt-2">No budget items yet.</div>}
      {items.length > 0 && (
        <table className="w-full text-xs">
          <thead className="text-[10px] uppercase tracking-widest text-neutral-500 border-b">
            <tr>
              <th className="text-left p-1">Category</th>
              <th className="text-left p-1">Description</th>
              <th className="text-right p-1">Planned</th>
              <th className="text-right p-1">Actual</th>
              <th className="text-left p-1">Vendor</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={i} className="border-b" data-testid={`exhibition-budget-${i}`}>
                <td className="p-1"><Input value={it.category} onChange={e => upd(i, { category: e.target.value })} onBlur={commit} className="h-7 rounded-none text-xs" placeholder="Booth setup / Travel / Marketing collateral" /></td>
                <td className="p-1"><Input value={it.description || ""} onChange={e => upd(i, { description: e.target.value })} onBlur={commit} className="h-7 rounded-none text-xs" /></td>
                <td className="p-1"><Input type="number" value={it.planned_amount || 0} onChange={e => upd(i, { planned_amount: Number(e.target.value) || 0 })} onBlur={commit} className="h-7 rounded-none text-xs text-right w-24 font-mono" /></td>
                <td className="p-1"><Input type="number" value={it.actual_amount || ""} onChange={e => upd(i, { actual_amount: Number(e.target.value) || null })} onBlur={commit} className="h-7 rounded-none text-xs text-right w-24 font-mono" /></td>
                <td className="p-1"><Input value={it.vendor || ""} onChange={e => upd(i, { vendor: e.target.value })} onBlur={commit} className="h-7 rounded-none text-xs" /></td>
                <td className="p-1 text-right"><button onClick={() => rm(i)} className="text-rose-600 hover:text-rose-700"><Trash2 className="w-3 h-3" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─────────────── Tab: Leads ───────────────

function LeadsTab({ row, ws, onOpenCapture }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get(`/workspaces/${ws}/exhibitions/${row.id}/leads`).then(r => setLeads(r.data.rows || [])).finally(() => setLoading(false));
  }, [ws, row.id]);
  return (
    <div className="border">
      <div className="flex items-center justify-between p-3 border-b bg-neutral-50/60">
        <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Captured leads ({leads.length})</div>
        <Button size="sm" className="rounded-none h-8" onClick={onOpenCapture} data-testid="exhibition-leads-capture-btn">
          <UserPlus className="w-3.5 h-3.5 mr-1" /> Capture lead
        </Button>
      </div>
      {loading && <div className="p-4 text-xs text-muted-foreground">Loading…</div>}
      {!loading && leads.length === 0 && (
        <div className="p-6 text-center text-sm text-muted-foreground">
          No leads captured yet. Use the button above during the event to log each meaningful conversation.
        </div>
      )}
      {leads.length > 0 && (
        <table className="w-full text-xs">
          <thead className="text-[10px] uppercase tracking-widest text-neutral-500 bg-neutral-50">
            <tr>
              <th className="text-left p-2">Name</th>
              <th className="text-left p-2">Company</th>
              <th className="text-left p-2">Email</th>
              <th className="text-left p-2">Phone</th>
              <th className="text-left p-2">Status</th>
              <th className="text-left p-2">Captured</th>
            </tr>
          </thead>
          <tbody>
            {leads.map(l => (
              <tr key={l.id} className="border-t">
                <td className="p-2 font-medium">{l.name}</td>
                <td className="p-2">{l.company || "—"}</td>
                <td className="p-2 text-muted-foreground">{l.email || "—"}</td>
                <td className="p-2 text-muted-foreground">{l.phone || "—"}</td>
                <td className="p-2"><span className="text-[10px] px-2 py-0.5 border">{l.status}</span></td>
                <td className="p-2 text-[10px] text-muted-foreground">{l.created_at?.slice(0, 10)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─────────────── Capture lead dialog ───────────────

function CaptureLeadDialog({ ws, eid, onClose, onDone }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", title: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    setBusy(true);
    try {
      await api.post(`/workspaces/${ws}/exhibitions/${eid}/capture-lead`, form);
      toast.success("Lead captured");
      onDone?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Capture failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !busy && !v && onClose()}>
      <DialogContent className="max-w-md rounded-none">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><UserPlus className="w-4 h-4" /> Capture a lead</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <div><Label className="text-xs">Name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="h-9 rounded-none" data-testid="capture-lead-name" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Company</Label><Input value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} className="h-9 rounded-none" /></div>
            <div><Label className="text-xs">Title</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="h-9 rounded-none" /></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Email</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="h-9 rounded-none" data-testid="capture-lead-email" /></div>
            <div><Label className="text-xs">Phone</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="h-9 rounded-none" data-testid="capture-lead-phone" /></div>
          </div>
          <div><Label className="text-xs">Notes</Label><Textarea rows={3} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="rounded-none" placeholder="Interested in enterprise plan · follow up next week" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button className="rounded-none" onClick={submit} disabled={busy} data-testid="capture-lead-submit"><Save className="w-4 h-4 mr-1" /> {busy ? "Saving…" : "Capture"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



// ─────────────── Tab: Playbook (checklists) ───────────────

function PlaybookTab({ row, ws, onReload }) {
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [appliedInfo, setAppliedInfo] = useState(null);
  useEffect(() => {
    api.get(`/workspaces/${ws}/exhibition-checklist-templates`).then(r => setTemplates(r.data || []));
  }, [ws]);
  const applied = new Set(row.applied_checklist_seed_ids || []);
  const toggle = (sid) => setSelected(s => { const n = new Set(s); n.has(sid) ? n.delete(sid) : n.add(sid); return n; });
  const apply = async () => {
    if (selected.size === 0) return toast.error("Pick at least one checklist");
    if (!row.linked_project_id) return toast.error("Link a Project on the Overview tab first — tasks are created there.");
    setBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${ws}/exhibitions/${row.id}/apply-checklist`, {
        template_seed_ids: [...selected],
      });
      setAppliedInfo(data);
      toast.success(`Created ${data.created} task${data.created === 1 ? "" : "s"} in linked project`);
      setSelected(new Set());
      onReload?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Apply failed")); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-3">
      <div className="border p-3 bg-neutral-50/50">
        <div className="text-xs">
          Applying a checklist creates tasks in the <b>linked Project</b> (currently: {row.linked_project_id ? <span className="text-emerald-600">linked ✓</span> : <span className="text-rose-600">NOT linked — set it on the Overview tab</span>}).
          Task due dates are computed from the exhibition&apos;s <b>start date</b> ({row.start_date}) using each item&apos;s offset.
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {templates.map(t => {
          const isApplied = applied.has(t.seed_id);
          return (
            <label key={t.seed_id} className={`border p-3 cursor-pointer hover:border-primary ${selected.has(t.seed_id) ? "border-primary bg-primary/5" : ""}`} data-testid={`checklist-${t.seed_id}`}>
              <div className="flex items-start gap-2">
                <input type="checkbox" checked={selected.has(t.seed_id)} onChange={() => toggle(t.seed_id)} className="mt-1" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm flex items-center gap-1">
                    <ListChecks className="w-3 h-3" /> {t.title}
                    {isApplied && <span className="text-[9px] px-1.5 py-0.5 bg-emerald-100 text-emerald-700 border border-emerald-300 ml-1">APPLIED</span>}
                  </div>
                  <div className="text-[11px] text-muted-foreground line-clamp-2 mt-1">{t.description}</div>
                  <div className="text-[10px] text-muted-foreground mt-1">{(t.items || []).length} tasks</div>
                </div>
              </div>
            </label>
          );
        })}
      </div>
      <div className="flex items-center gap-2">
        <Button className="rounded-none" onClick={apply} disabled={busy || selected.size === 0 || !row.linked_project_id} data-testid="playbook-apply">
          <Plus className="w-4 h-4 mr-1" /> {busy ? "Applying…" : `Apply ${selected.size} checklist${selected.size === 1 ? "" : "s"}`}
        </Button>
        {appliedInfo && (
          <div className="text-xs text-emerald-700">
            ✓ {appliedInfo.created} task{appliedInfo.created === 1 ? "" : "s"} created in the project
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────── Tab: ROI ───────────────

function RoiTab({ row, ws }) {
  const [rep, setRep] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get(`/workspaces/${ws}/exhibitions/${row.id}/roi-report`).then(r => setRep(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [ws, row.id]);
  if (loading) return <div className="p-4 text-sm text-muted-foreground">Computing ROI…</div>;
  if (!rep) return <div className="p-4 text-sm text-rose-600">Failed to load ROI report</div>;
  const l = rep.leads, b = rep.budget, ff = rep.footfall, c = rep.campaign;
  const attClr = (v) => v == null ? "text-neutral-400" : v >= 100 ? "text-emerald-600" : v >= 60 ? "text-amber-600" : "text-rose-600";
  return (
    <div className="space-y-4" data-testid="roi-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="border p-3">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1"><Trophy className="w-3 h-3" /> Leads captured</div>
          <div className="text-2xl font-mono mt-1">{l.captured}</div>
          <div className="text-[11px] text-muted-foreground">of {l.target ?? "—"} target · <span className={attClr(l.attainment_percent)}>{l.attainment_percent != null ? `${l.attainment_percent}%` : "—"}</span></div>
        </div>
        <div className="border p-3">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1"><Wallet className="w-3 h-3" /> Cost per lead</div>
          <div className="text-2xl font-mono mt-1">{b.cost_per_lead != null ? b.cost_per_lead.toLocaleString() : "—"}</div>
          <div className="text-[11px] text-muted-foreground">total spend ÷ leads</div>
        </div>
        <div className="border p-3">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Budget variance</div>
          <div className={`text-2xl font-mono mt-1 ${b.variance > 0 ? "text-rose-600" : "text-emerald-600"}`}>
            {b.variance > 0 ? "+" : ""}{b.variance.toLocaleString()}
          </div>
          <div className="text-[11px] text-muted-foreground">{b.variance_percent != null ? `${b.variance_percent}% vs planned` : "—"}</div>
        </div>
        <div className="border p-3">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1"><Users className="w-3 h-3" /> Footfall</div>
          <div className="text-2xl font-mono mt-1">{ff.actual_total ?? "—"}</div>
          <div className="text-[11px] text-muted-foreground">of {ff.expected_total ?? "—"} expected · <span className={attClr(ff.attainment_percent)}>{ff.attainment_percent != null ? `${ff.attainment_percent}%` : "—"}</span></div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="border p-4">
          <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">Lead funnel by status</div>
          {Object.keys(l.funnel || {}).length === 0 ? (
            <div className="text-xs text-muted-foreground italic">No leads captured yet.</div>
          ) : (
            <table className="w-full text-xs">
              <tbody>
                {Object.entries(l.funnel).sort((a, b) => b[1] - a[1]).map(([status, count]) => {
                  const pct = l.captured ? (100 * count / l.captured) : 0;
                  return (
                    <tr key={status} className="border-b last:border-0">
                      <td className="py-1.5 capitalize font-medium w-32">{status}</td>
                      <td className="py-1.5 relative"><div className="bg-blue-500/20 h-4" style={{ width: `${pct}%` }} /></td>
                      <td className="py-1.5 text-right font-mono w-16">{count}</td>
                      <td className="py-1.5 text-right text-muted-foreground w-14">{pct.toFixed(0)}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="border p-4">
          <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">Budget breakdown</div>
          <table className="w-full text-xs">
            <tbody>
              <tr className="border-b"><td className="py-1.5">Booth cost</td><td className="text-right font-mono">{b.booth_cost.toLocaleString()}</td></tr>
              <tr className="border-b"><td className="py-1.5">Sponsorship</td><td className="text-right font-mono">{b.sponsorship_cost.toLocaleString()}</td></tr>
              <tr className="border-b"><td className="py-1.5">Line-items (planned)</td><td className="text-right font-mono">{b.line_items_planned.toLocaleString()}</td></tr>
              <tr className="border-b"><td className="py-1.5">Line-items (actual)</td><td className="text-right font-mono">{b.line_items_actual.toLocaleString()}</td></tr>
              <tr className="border-t-2 font-semibold"><td className="py-1.5">Total planned</td><td className="text-right font-mono">{b.total_planned.toLocaleString()}</td></tr>
              <tr className="font-semibold"><td className="py-1.5">Total actual</td><td className={`text-right font-mono ${b.variance > 0 ? "text-rose-600" : "text-emerald-600"}`}>{b.total_actual.toLocaleString()}</td></tr>
            </tbody>
          </table>
        </div>
      </div>

      {c && (
        <div className="border p-4">
          <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">Post-event campaign · {c.name}</div>
          <div className="text-xs mb-2">Status: <b>{c.status}</b></div>
          <div className="grid grid-cols-4 gap-2">
            {Object.entries(c.stats || {}).slice(0, 8).map(([k, v]) => (
              <div key={k} className="border p-2 text-center">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{k}</div>
                <div className="font-mono text-sm">{v}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────── Convert-to-campaign dialog ───────────────

function ConvertToCampaignDialog({ ws, eid, exhibitionName, onClose, onDone }) {
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [campaignName, setCampaignName] = useState(`Post-event nurture · ${exhibitionName}`);
  const [fromName, setFromName] = useState("");
  const [fromNumber, setFromNumber] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    api.get(`/workspaces/${ws}/marketing/templates?channel=whatsapp`).then(r => setTemplates(r.data || []));
  }, [ws]);

  const submit = async () => {
    if (!templateId) return toast.error("Pick a WhatsApp template");
    setBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${ws}/exhibitions/${eid}/convert-to-campaign`, {
        template_id: templateId,
        campaign_name: campaignName.trim() || undefined,
        from_name: fromName.trim() || undefined,
        from_number: fromNumber.trim() || undefined,
      });
      setResult(data);
      toast.success(`Draft campaign created for ${data.target_count} leads`);
    } catch (e) { toast.error(apiErrorMessage(e, "Convert failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !busy && !v && onClose()}>
      <DialogContent className="max-w-md rounded-none">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Send className="w-4 h-4 text-emerald-600" /> Convert to Marketing Campaign</DialogTitle>
        </DialogHeader>
        {!result && (
          <div className="space-y-3">
            <div className="text-xs text-muted-foreground bg-emerald-50 border border-emerald-200 p-2">
              This creates a <b>draft WhatsApp campaign</b> auto-targeting every lead you captured at this expo. You&apos;ll still hit <b>Send</b> in the campaign editor to actually dispatch — think of it as a one-click setup.
            </div>
            {templates.length === 0 ? (
              <div className="text-xs text-rose-600 bg-rose-50 border border-rose-200 p-2">
                No WhatsApp templates in this workspace yet. Create one under Marketing → Templates first.
              </div>
            ) : (
              <>
                <div>
                  <Label className="text-xs">WhatsApp template *</Label>
                  <Select value={templateId} onValueChange={setTemplateId}>
                    <SelectTrigger className="h-9 rounded-none" data-testid="convert-template"><SelectValue placeholder="Pick a template…" /></SelectTrigger>
                    <SelectContent>
                      {templates.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Campaign name</Label>
                  <Input value={campaignName} onChange={e => setCampaignName(e.target.value)} className="h-9 rounded-none" data-testid="convert-name" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">From name</Label>
                    <Input value={fromName} onChange={e => setFromName(e.target.value)} className="h-9 rounded-none" />
                  </div>
                  <div>
                    <Label className="text-xs">From number</Label>
                    <Input value={fromNumber} onChange={e => setFromNumber(e.target.value)} className="h-9 rounded-none" placeholder="+91..." />
                  </div>
                </div>
              </>
            )}
          </div>
        )}
        {result && (
          <div className="space-y-3">
            <div className="border border-emerald-300 bg-emerald-50 p-3">
              <div className="text-sm font-semibold text-emerald-700">✓ Draft campaign created</div>
              <div className="text-xs mt-1">
                Segment <b>{result.segment_name}</b> with <b>{result.target_count}</b> lead{result.target_count === 1 ? "" : "s"} · Campaign <b>{result.campaign_name}</b>.
              </div>
            </div>
            <div className="text-xs text-muted-foreground">
              Next: open Marketing → Campaigns, review the draft, and hit <b>Send</b>. The linked campaign is now visible on the ROI tab of this exhibition.
            </div>
          </div>
        )}
        <DialogFooter>
          {result ? (
            <>
              <Button variant="outline" className="rounded-none" onClick={onDone}>Close</Button>
              <Button className="rounded-none" onClick={() => navigate("/app/marketing?tab=campaigns")} data-testid="convert-goto">Open Marketing</Button>
            </>
          ) : (
            <>
              <Button variant="outline" className="rounded-none" onClick={onClose} disabled={busy}>Cancel</Button>
              <Button className="rounded-none" onClick={submit} disabled={busy || templates.length === 0} data-testid="convert-submit">
                {busy ? "Creating…" : <><Send className="w-4 h-4 mr-1" /> Create draft campaign</>}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
